import { createSelector } from 'reselect'
import config from 'constants/config'
import { isBookingProtectionAvailableForAccommodationOffer } from 'lib/checkout/bookingProtection/utils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { isInsuranceBookingProtectionEnabledForRegion } from 'lib/insurance/insuranceHelpers'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import { isInsuranceBookingProtectionItem, isExperienceItem, isBNBLLEHotelItem, isLuxPlusSubscriptionItem, isSubscriptionJoinItem, isInsuranceItem, isInstantBookingLEHotelItem, isBedbankItem, isAccommodationV2Item, isTransferItem } from 'lib/checkout/checkoutUtils'
import isCheckoutEsimUpsellExperienceItem from 'loyalty/lib/esims/esimCheckoutUtils'
import { min, nonNullable } from 'lib/array/arrayUtils'
import type { RegionalRuleOptions } from 'lib/insurance/verifyItemFollowsRegionalRules'
import verifyItemFollowsRegionalRules from 'lib/insurance/verifyItemFollowsRegionalRules'
import { isSpoofed } from 'selectors/isSpoofed'
import { STANDALONE_FLIGHT_CFMR_REGIONS } from 'constants/config/region'
import { shouldOfferBookingProtectionForFijiAndMaldives } from './sideBySideInsuranceContext'

export const isInsuranceBookingProtectionAvailableForCartItemTypes = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => isStandaloneExperience(state),
  (items, offers, isGift, isStandaloneExperience) => {
    if (isGift) {
      return false
    }

    const isInsurancePostPurchase = items.every(item => isInsuranceBookingProtectionItem(item) || isInsuranceItem(item))
    if (isInsurancePostPurchase) return true

    const filteredItems = items.filter(item =>
      !isLuxPlusSubscriptionItem(item) &&
      !isSubscriptionJoinItem(item) &&
      !isInsuranceBookingProtectionItem(item) &&
      !isInsuranceItem(item) &&
      !isTransferItem(item) &&
      !isCheckoutEsimUpsellExperienceItem(item),
    )

    if (!filteredItems.length) return false

    for (const item of filteredItems) {
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, item.itemType)) {
        return false
      }

      // Experiences that are BNBL are not eligible
      if (isExperienceItem(item) && item.isBuyNowBookLater) {
        return false
      }

      // BNB LE Hotel items are not eligible
      if (isBNBLLEHotelItem(item)) {
        return false
      }

      // For any hotel item, ensure the offer supports booking protection
      if (isInstantBookingLEHotelItem(item) || isBedbankItem(item)) {
        const offer = offers[item.offerId]
        if (offer && !isBookingProtectionAvailableForAccommodationOffer(offer)) {
          return false
        }
      }
    }

    if (isStandaloneExperience) {
      const experienceItemType: App.Checkout.ExperienceItemType = 'experience'
      if (!isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_BOOKING_PROTECTION_ENABLED_LIST, experienceItemType)) return false
    }

    return true
  },
)

const getOfferCountryCode = (
  item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem | App.Checkout.AccommodationV2Item,
  offers: { [offerId: string]: App.Offer | App.BedbankOffer },
  properties: App.OfferState['properties'],
): string | undefined => {
  if (isAccommodationV2Item(item)) {
    return properties[item.propertyId]?.property?.location?.countryCode
  }

  const offer: any = offers[item.offerId]

  if (isInstantBookingLEHotelItem(item)) {
    return offer?.property?.geoData?.countryCode
  }

  if (isBedbankItem(item)) {
    return offer?.property?.address?.countryCode
  }
}
/**
 * Whether every hotel in the cart may be sold cancellation protection under the destination
 * rules. `offerFijiAndMaldives` lifts the Maldives and Fiji exclusions.
 */
function createIsBookingProtectionAvailableForHotelRegion(options: RegionalRuleOptions) {
  return createSelector(
    (state: App.State) => state.checkout.cart.items,
    (state: App.State) => state.offer.offers,
    (state: App.State) => state.offer.bedbankOffers,
    (state: App.State) => state.offer.properties,
    (items, leOffers, bedbankOffers, properties) => {
      // Filter hotel items (LE hotel, bedbank, or accommodationV2)
      const hotelItems = items.filter(item => isBedbankItem(item) || isInstantBookingLEHotelItem(item) || isAccommodationV2Item(item))

      // Collect earliest check-in date from hotel items (Instant LE Hotel, bedbank, or accommodationV2)
      const checkInDates = nonNullable(
        hotelItems.map(item => {
          if (isInstantBookingLEHotelItem(item) || isBedbankItem(item) || isAccommodationV2Item(item)) {
            return new Date(item.checkIn)
          }
        }),
      )
      const earliestCheckinDate = min(checkInDates)

      const offers = { ...leOffers, ...bedbankOffers }

      for (const item of hotelItems) {
        const countryCode = getOfferCountryCode(item, offers, properties)
        const isValidRegionRules = verifyItemFollowsRegionalRules(countryCode, earliestCheckinDate, options)
        if (!isValidRegionRules) return false
      }

      return true
    },
  )
}

const isBookingProtectionAvailableForHotelRegion = createIsBookingProtectionAvailableForHotelRegion({})
const isBookingProtectionAvailableForHotelRegionIncludingFijiAndMaldives = createIsBookingProtectionAvailableForHotelRegion({ offerFijiAndMaldives: true })

/**
 * Whether the current region offers CFMR (insurance booking-protection) as the insurance
 * option on standalone flights checkout. Used to relax the standalone-flights guards so the
 * CFMR component is shown (regions like AU/NZ continue to use travel protection instead).
 */
export const isStandaloneFlightCfmrRegion = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode): boolean => STANDALONE_FLIGHT_CFMR_REGIONS.includes(currentRegionCode),
)

/**
 * Whether CFMR (insurance booking protection) is sold in the customer's current region, independent
 * of what is in the cart. svc-insurance rejects CFMR quotes from any other origin, so this gates
 * every CFMR quote made outside checkout (flight search Protect fares, bedbank flex packages).
 *
 * Deliberately not memoised: the answer is a boolean derived from the region code and live config,
 * so caching it per region would only pin a stale config value.
 */
export function isInsuranceBookingProtectionEnabledForCurrentRegion(state: App.State): boolean {
  return isInsuranceBookingProtectionEnabledForRegion(state.geo.currentRegionCode)
}

function createIsInsuranceBookingProtectionAvailableForRegions(
  hotelRegionSelector: (state: App.State) => boolean,
) {
  return createSelector(
    (state: App.State) => isInsuranceBookingProtectionEnabledForCurrentRegion(state),
    hotelRegionSelector,
    (state: App.State) => isSpoofed(state),
    (isEnabledForCurrentRegion, isBookingProtectionAvailableForHotelOfferEligibility, isSpoofedUser) => {
      if (!isEnabledForCurrentRegion) return false
      if (!isBookingProtectionAvailableForHotelOfferEligibility && !isSpoofedUser) return false
      return true
    },
  )
}

/**
 * Region availability with the Maldives and Fiji exclusions lifted. Drives the side-by-side
 * insurance eligibility so that both experiment cohorts are judged on the same audience.
 */
export const isInsuranceBookingProtectionAvailableForRegionsIncludingFijiAndMaldives =
  createIsInsuranceBookingProtectionAvailableForRegions(isBookingProtectionAvailableForHotelRegionIncludingFijiAndMaldives)

const isInsuranceBookingProtectionAvailableForRegionsExcludingFijiAndMaldives =
  createIsInsuranceBookingProtectionAvailableForRegions(isBookingProtectionAvailableForHotelRegion)

/**
 * Whether cancellation protection can be sold in the customer's region for the hotels in the
 * cart. Maldives and Fiji only qualify for customers seeing the side-by-side insurance variant.
 */
export const isInsuranceBookingProtectionAvailableForRegions = createSelector(
  (state: App.State) => isInsuranceBookingProtectionAvailableForRegionsExcludingFijiAndMaldives(state),
  (state: App.State) => isInsuranceBookingProtectionAvailableForRegionsIncludingFijiAndMaldives(state),
  (state: App.State) => shouldOfferBookingProtectionForFijiAndMaldives(state),
  (excludingFijiAndMaldives, includingFijiAndMaldives, offerFijiAndMaldives): boolean =>
    offerFijiAndMaldives ? includingFijiAndMaldives : excludingFijiAndMaldives,
)
